import React from 'react';
import jsPDF from 'jspdf';
import { loadLogo } from './pdfUtils';
import { PAGE } from './sldLayout';

// Renders the sheets from sldLayout.js — as SVG for the screen, and with jsPDF for printing

const PT_TO_MM = 0.3528;

// Items of a sheet with the overview's scale / offset applied
function flatten(page) {
  const out = [...(page.frame || []), ...page.items];
  const g = page.group;
  if (g) {
    const X = (x) => g.dx + x * g.s, Y = (y) => g.dy + y * g.s;
    for (const it of g.items) {
      if (it.t === 'line') out.push({ ...it, x1: X(it.x1), y1: Y(it.y1), x2: X(it.x2), y2: Y(it.y2), w: it.w * Math.max(g.s, 0.6) });
      else if (it.t === 'circle') out.push({ ...it, cx: X(it.cx), cy: Y(it.cy), r: it.r * g.s, w: it.w * Math.max(g.s, 0.6) });
      else if (it.t === 'rect') out.push({ ...it, x: X(it.x), y: Y(it.y), w: it.w * g.s, h: it.h * g.s });
      else if (it.t === 'tri') out.push({ ...it, pts: it.pts.map(([x, y]) => [X(x), Y(y)]) });
      else if (it.t === 'text') out.push({ ...it, x: X(it.x), y: Y(it.y), size: it.size * g.s });
    }
  }
  return out;
}

export function SldSvg({ page, className }) {
  const items = flatten(page);
  return (
    <svg className={className} viewBox={`0 0 ${PAGE.w} ${PAGE.h}`} xmlns="http://www.w3.org/2000/svg">
      <rect x={0} y={0} width={PAGE.w} height={PAGE.h} fill="#fff" />
      {items.map((it, i) => {
        if (it.t === 'line') return <line key={i} x1={it.x1} y1={it.y1} x2={it.x2} y2={it.y2} stroke="#000" strokeWidth={it.w} strokeDasharray={it.dash ? '1.5 1' : undefined} strokeLinecap="butt" />;
        if (it.t === 'rect') return <rect key={i} x={it.x} y={it.y} width={it.w} height={it.h} fill="none" stroke="#000" strokeWidth={it.lw} />;
        if (it.t === 'circle') return <circle key={i} cx={it.cx} cy={it.cy} r={it.r} fill="none" stroke="#000" strokeWidth={it.w} />;
        if (it.t === 'tri') return <polygon key={i} points={it.pts.map(p => p.join(',')).join(' ')} fill="#000" />;
        if (it.t === 'logo') return <image key={i} href="/kse-logo.svg" x={it.x} y={it.y} width={it.w} height={it.h} />;
        if (it.t === 'text') {
          const anchor = { start: 'start', middle: 'middle', end: 'end' }[it.anchor];
          return (
            <text key={i} x={it.x} y={it.y} fontSize={it.size * PT_TO_MM} fontFamily="Helvetica, Arial, sans-serif"
              fontWeight={it.bold ? 700 : 400} textAnchor={anchor} fill="#000"
              transform={it.angle ? `rotate(${-it.angle} ${it.x} ${it.y})` : undefined}>{it.s}</text>
          );
        }
        return null;
      })}
    </svg>
  );
}

// The built-in PDF font is WinAnsi only
const clean = (s) => s.replace(/→/g, '->').replace(/↔/g, '<->').replace(/[φΦ]/g, 'phi').replace(/Ω/g, 'Ohm')
  .replace(/[^\x00-\xFF–—•…€]/g, '?');

export async function buildSldPdf(pages) {
  const logo = await loadLogo();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
  pages.forEach((page, pi) => {
    if (pi > 0) doc.addPage('a3', 'landscape');
    doc.setDrawColor(0); doc.setFillColor(0); doc.setTextColor(0);
    for (const it of flatten(page)) {
      if (it.t === 'line') {
        doc.setLineWidth(it.w);
        doc.setLineDashPattern(it.dash ? [1.5, 1] : [], 0);
        doc.line(it.x1, it.y1, it.x2, it.y2);
        if (it.dash) doc.setLineDashPattern([], 0);
      } else if (it.t === 'rect') {
        doc.setLineWidth(it.lw); doc.rect(it.x, it.y, it.w, it.h, 'S');
      } else if (it.t === 'circle') {
        doc.setLineWidth(it.w); doc.circle(it.cx, it.cy, it.r, 'S');
      } else if (it.t === 'tri') {
        const [[x1, y1], [x2, y2], [x3, y3]] = it.pts; doc.triangle(x1, y1, x2, y2, x3, y3, 'F');
      } else if (it.t === 'logo') {
        if (logo) doc.addImage(logo, 'PNG', it.x, it.y, it.w, it.h);
      } else if (it.t === 'text') {
        doc.setFont('helvetica', it.bold ? 'bold' : 'normal');
        doc.setFontSize(it.size);
        const opts = it.angle ? { angle: it.angle } : { align: it.anchor === 'middle' ? 'center' : it.anchor === 'end' ? 'right' : 'left' };
        doc.text(clean(it.s), it.x, it.y, opts);
      }
    }
  });
  return doc.output('blob');
}
